export type HelpAction =
  | { type: "open_tool"; tool: "text" | "image" | "clipart" | "shapes" | "background"; textRole?: "heading" | "body" | "list" }
  | { type: "open_templates"; tab: "themes" | "spreads" }
  | { type: "open_share" };

export type HelpFaqEntry = {
  id: string;
  title: { pl: string; en: string };
  keywords: { pl: string[]; en: string[] };
  answer: { pl: string; en: string };
  related?: string[];
  action?: HelpAction;
  actionLabel?: { pl: string; en: string };
};

export const FAQ_ENTRIES: HelpFaqEntry[] = [
  {
    id: "font-size",
    title: {
      pl: "Zmiana wielkości czcionki",
      en: "Change font size",
    },
    keywords: {
      pl: ["czcionka", "wielkość czcionki", "rozmiar czcionki", "powiększyć czcionkę", "tekst większy", "font size"],
      en: ["font", "font size", "text size", "bigger text", "increase font"],
    },
    answer: {
      pl: "Kliknij tekst na canvasie, aby otworzyć panel właściwości. W sekcji tekstu użyj suwaka „Rozmiar” lub wpisz większą wartość w punktach (pt).",
      en: "Click the text on the canvas to open Properties. In the text section use the Size slider or enter a bigger value in pt.",
    },
    related: ["font-family", "line-height", "text-role"],
  },
  {
    id: "font-family",
    title: {
      pl: "Zmiana czcionki",
      en: "Change font family",
    },
    keywords: {
      pl: ["czcionka", "font", "zmiana czcionki", "rodzaj czcionki", "krój pisma"],
      en: ["font", "font family", "typeface", "change font"],
    },
    answer: {
      pl: "Kliknij tekst, a następnie w panelu właściwości otwórz listę czcionek. Lista pokazuje podgląd każdej czcionki. Wybierz pozycję, aby zastosować ją do zaznaczonego tekstu.",
      en: "Click the text, then open the font list in Properties. The list shows a live preview for each font. Select one to apply it to the selected text.",
    },
    related: ["font-size", "text-role"],
  },
  {
    id: "line-height",
    title: {
      pl: "Zmiana interlinii",
      en: "Change line height",
    },
    keywords: {
      pl: ["interlinia", "odstęp między liniami", "line height", "wiersze"],
      en: ["line height", "line spacing", "spacing between lines"],
    },
    answer: {
      pl: "Kliknij tekst i w panelu właściwości użyj suwaka „Interlinia”. Standardowa wartość to około 1.5. Dla nagłówków zwykle lepiej sprawdza się mniejsza wartość.",
      en: "Click the text and use the Line Height slider in Properties. A standard value is around 1.5. Headings usually work better with a smaller value.",
    },
    related: ["font-size", "text-role"],
  },
  {
    id: "insert-image",
    title: {
      pl: "Dodawanie zdjęcia",
      en: "Insert image",
    },
    keywords: {
      pl: ["dodać zdjęcie", "wgrać zdjęcie", "upload zdjęcia", "obraz", "image"],
      en: ["add image", "upload image", "photo", "insert photo"],
    },
    answer: {
      pl: "W lewym panelu kliknij Zdjęcia. Możesz wgrać jedno zdjęcie albo wiele zdjęć naraz. Kliknięcie miniatury wstawia zdjęcie do projektu, a bibliotekę zostawiamy otwartą, żeby można było szybko dodać kolejne zdjęcia. Możesz też przeciągnąć miniaturę bezpośrednio na stronę.",
      en: "In the left panel click Images. You can upload one image or many at once. Clicking a thumbnail inserts the photo into the project and keeps the library open so you can quickly add more photos. You can also drag a thumbnail directly onto the page.",
    },
    related: ["image-library", "crop-image", "image-mask"],
  },
  {
    id: "image-library",
    title: {
      pl: "Korzystanie z biblioteki zdjęć",
      en: "Use the image library",
    },
    keywords: {
      pl: ["biblioteka zdjęć", "miniatury", "ukryj użyte zdjęcia", "użyte zdjęcia", "wiele zdjęć", "drag zdjęcia", "przeciągnij zdjęcie"],
      en: ["image library", "photo library", "thumbnails", "hide used images", "used images", "many photos", "drag photo"],
    },
    answer: {
      pl: "Biblioteka pokazuje lekkie miniatury, a oryginalne zdjęcie jest pobierane dopiero przy wstawieniu lub podglądzie lupą. Opcja „Ukryj użyte zdjęcia” jest domyślnie włączona, więc widzisz głównie zdjęcia, których jeszcze nie ma w projekcie. Po wstawieniu zdjęcia biblioteka pozostaje otwarta. Panel właściwości zdjęcia otworzy się dopiero po kliknięciu zdjęcia na canvasie.",
      en: "The library shows lightweight thumbnails, and the original image is fetched only when you insert it or open the magnifier preview. Hide used images is enabled by default, so you mostly see photos that are not yet in the project. After inserting a photo, the library stays open. Image properties open only after you click the photo on the canvas.",
    },
    related: ["insert-image", "replace-placeholder", "crop-image"],
    action: { type: "open_tool", tool: "image" },
    actionLabel: {
      pl: "Pokaż bibliotekę zdjęć",
      en: "Show image library",
    },
  },
  {
    id: "crop-image",
    title: {
      pl: "Kadrowanie zdjęcia",
      en: "Crop image",
    },
    keywords: {
      pl: ["kadrowanie", "crop", "przyciąć zdjęcie", "przesunąć zdjęcie", "zoom zdjęcia"],
      en: ["crop", "crop image", "move image", "zoom image"],
    },
    answer: {
      pl: "Kliknij zdjęcie, a potem w pasku narzędzi zdjęcia wybierz Kadruj. W trybie kadrowania możesz używać przybliżenia i przesuwać obraz wewnątrz ramki. Zatwierdź przyciskiem Gotowe.",
      en: "Click the image, then use Crop in the image toolbar. In crop mode you can zoom and pan the image inside the frame. Confirm with Done.",
    },
    related: ["insert-image", "image-mask"],
  },
  {
    id: "image-mask",
    title: {
      pl: "Maska zdjęcia",
      en: "Image mask",
    },
    keywords: {
      pl: ["maska", "kształt zdjęcia", "serce", "oval", "shape mask"],
      en: ["mask", "shape mask", "heart mask", "oval mask"],
    },
    answer: {
      pl: "Kliknij zdjęcie i w panelu właściwości wybierz sekcję maski. Możesz ustawić brak maski albo jeden z kształtów, np. serce, owal lub półkole.",
      en: "Click the image and open the mask section in Properties. You can choose no mask or a shape mask like heart, oval or semicircle.",
    },
    related: ["crop-image", "shapes"],
  },
  {
    id: "rotate-element",
    title: {
      pl: "Obrót elementu",
      en: "Rotate element",
    },
    keywords: {
      pl: ["obrót", "obrócić", "rotate", "kąt", "rotate handle"],
      en: ["rotate", "rotation", "angle", "rotate handle"],
    },
    answer: {
      pl: "Zaznacz element i użyj uchwytu rotate. Przytrzymaj Shift, aby obrót przyciągał się co 15°. Dokładny kąt możesz też wpisać w panelu właściwości.",
      en: "Select an element and use the rotate handle. Hold Shift to snap rotation to 15°. You can also enter an exact angle in Properties.",
    },
    related: ["resize-element"],
  },
  {
    id: "resize-element",
    title: {
      pl: "Zmiana rozmiaru elementu",
      en: "Resize element",
    },
    keywords: {
      pl: ["resize", "zmiana rozmiaru", "powiększyć zdjęcie", "rozciągnąć element", "uchwyty"],
      en: ["resize", "scale", "change size", "stretch element", "handles"],
    },
    answer: {
      pl: "Zaznacz element i przeciągnij uchwyty resize. Dla tekstu i obrazów rozmiar można też zmienić w panelu właściwości. Tekst trzyma się guide marginesów.",
      en: "Select the element and drag the resize handles. For text and images you can also change size in Properties. Text stays inside the margin guide.",
    },
    related: ["rotate-element", "text-guides"],
  },
  {
    id: "text-guides",
    title: {
      pl: "Marginesy i obramowanie tekstu",
      en: "Text guides and margins",
    },
    keywords: {
      pl: ["marginesy", "guide", "ramka tekstu", "obramowanie tekstu", "padding tekstu"],
      en: ["margins", "guide", "text frame", "text padding", "text guide"],
    },
    answer: {
      pl: "Tekst w szablonach i podczas edycji korzysta z guide marginesów strony. Wewnętrzny dashed guide pokazuje padding tekstu. Dzięki temu tekst nie wychodzi poza bezpieczny obszar.",
      en: "Text uses page margin guides. The inner dashed guide shows text padding. This keeps text inside the safe content area.",
    },
    related: ["font-size", "line-height"],
  },
  {
    id: "templates",
    title: {
      pl: "Szablony i tematy",
      en: "Templates and themes",
    },
    keywords: {
      pl: ["szablon", "template", "theme", "temat", "rozkładówka", "insert spread"],
      en: ["template", "theme", "spread", "insert spread"],
    },
    answer: {
      pl: "W panelu Szablony masz dwa typy: Motywy i Rozkładówki. Motyw buduje cały startowy układ projektu. Rozkładówka wstawia pojedynczy układ na końcu.",
      en: "The Templates panel has two types: Themes and Spreads. A Theme builds a starting project layout. A Spread inserts one spread at the end.",
    },
    related: ["undo-theme", "text-guides"],
  },
  {
    id: "undo-theme",
    title: {
      pl: "Cofnięcie ostatniego motywu",
      en: "Undo last theme",
    },
    keywords: {
      pl: ["cofnąć motyw", "undo theme", "cofnij szablon", "undo template"],
      en: ["undo theme", "undo template", "revert theme"],
    },
    answer: {
      pl: "Po zastosowaniu motywu otwórz Szablony → Motywy i użyj przycisku Cofnij ostatni motyw. Przywraca on poprzedni stan projektu o jeden krok.",
      en: "After applying a theme, open Templates → Themes and use Undo last theme. It restores the previous project state by one step.",
    },
    related: ["templates"],
  },
  {
    id: "zoom-editor",
    title: {
      pl: "Powiększanie i pomniejszanie widoku",
      en: "Zoom in and out",
    },
    keywords: {
      pl: ["zoom", "powiększenie", "pomniejszenie", "widok", "suwak zoom", "przybliżyć"],
      en: ["zoom", "zoom in", "zoom out", "view scale"],
    },
    answer: {
      pl: "Nad obszarem projektu użyj suwaka przybliżenia. Po ręcznej zmianie pojawią się też przyciski Wyśrodkuj i Resetuj.",
      en: "Use the zoom slider above the canvas. After manual zooming you will also see Center and Reset.",
    },
    related: ["rotate-element", "resize-element"],
  },
  {
    id: "add-background",
    title: {
      pl: "Dodanie tła strony",
      en: "Add page background",
    },
    keywords: {
      pl: ["tło", "background", "kolor tła", "dodaj tło", "strona tło"],
      en: ["background", "page background", "background color"],
    },
    answer: {
      pl: "W lewym panelu wybierz narzędzie Tło. Element tła jest zawsze w najniższej warstwie i może mieć własny kolor.",
      en: "In the left panel choose the Background tool. Background always stays on the back layer and can have its own color.",
    },
    related: ["shapes", "image-mask"],
    action: { type: "open_tool", tool: "background" },
    actionLabel: {
      pl: "Pokaż narzędzie tła",
      en: "Show background tool",
    },
  },
  {
    id: "background-patterns",
    title: {
      pl: "Tło z wzorem",
      en: "Pattern background",
    },
    keywords: {
      pl: ["pattern", "patterns", "wzór", "wzory", "tło z wzorem", "svg", "png", "powtarzalne tło"],
      en: ["pattern", "patterns", "background pattern", "svg", "png", "repeatable background"],
    },
    answer: {
      pl: "W narzędziu Tło możesz wybrać Jednolity kolor albo Tło z wzorem. Wzory są kafelkami powtarzanymi na stronie, więc nie tracą jakości i działają też w PDF.",
      en: "In Background you can choose Solid color or Pattern background. Patterns are repeated tiles, so they stay sharp and also render in the PDF.",
    },
    related: ["add-background", "bleed-and-trim"],
    action: { type: "open_tool", tool: "background" },
    actionLabel: {
      pl: "Pokaż tła",
      en: "Show backgrounds",
    },
  },
  {
    id: "shapes",
    title: {
      pl: "Dodawanie kształtów",
      en: "Add shapes",
    },
    keywords: {
      pl: ["kształt", "shape", "serce", "gwiazda", "oval", "crescent", "dodać kształt"],
      en: ["shape", "add shape", "heart", "star", "oval", "crescent"],
    },
    answer: {
      pl: "W lewym panelu kliknij Kształty. Otworzy się biblioteka kształtów, z której możesz dodać np. koło, serce, owal lub półkole.",
      en: "In the left panel click Shapes. A shape library opens where you can add items like circle, heart, oval or semicircle.",
    },
    related: ["image-mask"],
    action: { type: "open_tool", tool: "shapes" },
    actionLabel: {
      pl: "Pokaż kształty",
      en: "Show shapes",
    },
  },
  {
    id: "clipart",
    title: {
      pl: "Dodawanie clipartów",
      en: "Add clipart",
    },
    keywords: {
      pl: ["clipart", "ilustracje", "gotowe ilustracje", "grafiki", "naklejki", "obrazki"],
      en: ["clipart", "illustrations", "ready-made illustrations", "stickers", "graphics"],
    },
    answer: {
      pl: "Kliknij Clipart w panelu narzędzi. Biblioteka jest ładowana z manifestu, pokazuje kategorie i podkategorie, a wybrany clipart trafia na stronę jak zwykły element: możesz go przesuwać, skalować i obracać.",
      en: "Click Clipart in the tools panel. The library is loaded from a manifest with categories and subcategories. Inserted clipart behaves like a normal element: you can move, resize and rotate it.",
    },
    related: ["resize-element", "rotate-element", "shapes"],
    action: { type: "open_tool", tool: "clipart" },
    actionLabel: {
      pl: "Pokaż cliparty",
      en: "Show clipart",
    },
  },
  {
    id: "templates-themes",
    title: {
      pl: "Motywy i szablony",
      en: "Themes and templates",
    },
    keywords: {
      pl: ["motyw", "theme", "szablon", "templates", "rozpocząć od motywu"],
      en: ["theme", "template", "templates", "start from theme"],
    },
    answer: {
      pl: "W Szablonach znajdziesz Motywy i Rozkładówki. Motyw buduje startowy projekt, a Rozkładówka wstawia jedną rozkładówkę na końcu.",
      en: "Templates contain Themes and Spreads. A Theme builds a starting project, while a Spread inserts one spread at the end.",
    },
    related: ["undo-theme", "insert-spread"],
    action: { type: "open_templates", tab: "themes" },
    actionLabel: {
      pl: "Pokaż motywy",
      en: "Show themes",
    },
  },
  {
    id: "insert-spread",
    title: {
      pl: "Dodanie rozkładówki z szablonu",
      en: "Insert a spread template",
    },
    keywords: {
      pl: ["rozkładówka", "spread", "dodaj spread", "insert spread", "template spread"],
      en: ["spread", "insert spread", "spread template"],
    },
    answer: {
      pl: "W Szablonach przejdź do zakładki Rozkładówki. Wybór szablonu doda nową rozkładówkę na końcu projektu.",
      en: "Open Templates and switch to the Spreads tab. Choosing a template inserts a new spread at the end of the project.",
    },
    related: ["templates-themes"],
    action: { type: "open_templates", tab: "spreads" },
    actionLabel: {
      pl: "Pokaż rozkładówki",
      en: "Show spreads",
    },
  },
  {
    id: "template-thumbnails",
    title: {
      pl: "Miniatury motywów i rozkładówek",
      en: "Theme and spread thumbnails",
    },
    keywords: {
      pl: ["miniatury", "thumbnail", "thumbnails", "podgląd rozkładówki", "podgląd motywu", "rozkładówki z obrazkiem"],
      en: ["thumbnails", "thumbnail", "spread preview", "theme preview", "template preview"],
    },
    answer: {
      pl: "Rozkładówki mają miniatury generowane z bloków szablonu. Motywy mogą mieć ręcznie dodane miniatury. Dzięki temu wybierasz układ po podglądzie, a nie tylko po nazwie.",
      en: "Spread thumbnails are generated from template blocks. Themes can use manually added thumbnails. This lets you choose a layout from a preview instead of only from its name.",
    },
    related: ["templates-themes", "insert-spread"],
    action: { type: "open_templates", tab: "spreads" },
    actionLabel: {
      pl: "Pokaż rozkładówki",
      en: "Show spreads",
    },
  },
  {
    id: "replace-placeholder",
    title: {
      pl: "Zastąpienie pustego miejsca zdjęciem",
      en: "Replace a placeholder with an image",
    },
    keywords: {
      pl: ["placeholder", "puste zdjęcie", "slot na zdjęcie", "zamień placeholder", "wstaw zdjęcie w szablon"],
      en: ["placeholder", "replace placeholder", "photo slot", "template image"],
    },
    answer: {
      pl: "Kliknij dwa razy puste miejsce na zdjęcie albo otwórz Zdjęcia i wybierz miniaturę z biblioteki. Puste miejsce zostanie zastąpione wybranym obrazem. Jeśli chcesz od razu zmienić kadr lub właściwości, kliknij wstawione zdjęcie na canvasie.",
      en: "Double-click the empty image slot or open Images and choose a thumbnail from the library. The placeholder will be replaced by the selected image. To adjust crop or properties, click the inserted photo on the canvas.",
    },
    related: ["insert-image", "image-library", "crop-image"],
    action: { type: "open_tool", tool: "image" },
    actionLabel: {
      pl: "Pokaż bibliotekę zdjęć",
      en: "Show image library",
    },
  },
  {
    id: "copy-paste-text",
    title: {
      pl: "Kopiowanie i wklejanie tekstu",
      en: "Copy and paste text elements",
    },
    keywords: {
      pl: ["copy", "paste", "duplikuj tekst", "kopiuj tekst", "ctrl c", "ctrl v"],
      en: ["copy", "paste", "duplicate text", "ctrl c", "ctrl v"],
    },
    answer: {
      pl: "Po zaznaczeniu tekstu pojawi się pasek narzędzi z opcjami Kopiuj, Wklej i Duplikuj. Działają też skróty klawiaturowe.",
      en: "When text is selected a toolbar appears with Copy, Paste and Duplicate. Keyboard shortcuts also work.",
    },
    related: ["font-size"],
  },
  {
    id: "share-project",
    title: {
      pl: "Udostępnienie projektu",
      en: "Share the project",
    },
    keywords: {
      pl: ["share", "udostępnij", "zaproś", "invite", "viewer", "editor", "link do projektu"],
      en: ["share", "invite", "viewer", "editor", "project link"],
    },
    answer: {
      pl: "Kliknij Udostępnij w górnym pasku. Możesz utworzyć link dla osoby oglądającej albo edytującej, skopiować go i później odwołać dostęp przyciskiem Cofnij dostęp.",
      en: "Click Share in the top bar. You can create a Viewer or Editor link, copy it and revoke access later.",
    },
    related: ["viewer-vs-editor", "lock-info"],
    action: { type: "open_share" },
    actionLabel: {
      pl: "Pokaż udostępnianie",
      en: "Show sharing",
    },
  },
  {
    id: "viewer-vs-editor",
    title: {
      pl: "Różnica między oglądającym i edytującym",
      en: "Difference between Viewer and Editor",
    },
    keywords: {
      pl: ["viewer", "editor", "różnica viewer editor", "uprawnienia"],
      en: ["viewer", "editor", "permissions", "difference viewer editor"],
    },
    answer: {
      pl: "Osoba oglądająca może przeglądać projekt, ale nie zapisuje zmian. Osoba edytująca może pracować nad projektem, ale nie może złożyć zamówienia. Zamówienie pozostaje po stronie właściciela projektu.",
      en: "A Viewer can inspect the project but cannot save changes. An Editor can work on the project but cannot place the order. Ordering remains restricted to the owner.",
    },
    related: ["share-project", "lock-info"],
  },
  {
    id: "lock-info",
    title: {
      pl: "Dlaczego projekt jest zablokowany",
      en: "Why the project is locked",
    },
    keywords: {
      pl: ["locked", "zablokowany", "ktoś edytuje", "locked by", "blokada projektu"],
      en: ["locked", "locked by", "someone is editing", "project lock"],
    },
    answer: {
      pl: "Jeśli inna osoba aktywnie edytuje projekt, możesz zobaczyć komunikat „Zablokowane przez ...”. To chroni projekt przed nadpisaniem zmian przez kilka osób jednocześnie.",
      en: "If another person is actively editing the project, you may see a Locked by ... message. This prevents conflicting autosaves from multiple users.",
    },
    related: ["share-project", "viewer-vs-editor"],
  },
  {
    id: "save-project",
    title: {
      pl: "Zapisywanie projektu",
      en: "Save project",
    },
    keywords: {
      pl: ["save", "zapisz", "zapisywanie", "autosave", "zapisz jako", "kopia projektu"],
      en: ["save", "autosave", "saving", "save as", "project copy"],
    },
    answer: {
      pl: "Projekt zapisuje się automatycznie podczas pracy. Przycisk Zapisz zapisuje zmiany w bieżącym projekcie. Aby zachować oryginał i pracować na niezależnej kopii, wybierz Zapisz jako, podaj nową nazwę i kliknij Zapisz i otwórz kopię. Kopia zawiera aktualne strony, okładkę i bibliotekę zdjęć. Funkcja jest dostępna właścicielowi projektu i administratorowi.",
      en: "The project is autosaved while you work. Save stores changes in the current project. To keep the original and work on an independent copy, choose Save as, enter a new name and click Save and open copy. The copy includes the current pages, cover and photo library. This feature is available to the project owner and administrators.",
    },
    related: ["share-project"],
  },
  {
    id: "list-marker",
    title: {
      pl: "Lista: wypunktowana lub numerowana",
      en: "List: bullets or numbers",
    },
    keywords: {
      pl: ["lista", "bullets", "numbers", "numerowana lista", "punkty listy"],
      en: ["list", "bullets", "numbers", "numbered list"],
    },
    answer: {
      pl: "Dla elementu typu Lista możesz wybrać wypunktowanie albo numerowanie w panelu właściwości. Każda linia tekstu staje się osobnym punktem listy.",
      en: "For a List element you can choose bullets or numbers in Properties. Each line becomes one list item.",
    },
    related: ["font-size", "line-height"],
    action: { type: "open_tool", tool: "text", textRole: "list" },
    actionLabel: {
      pl: "Pokaż narzędzia tekstu",
      en: "Show text tools",
    },
  },
  {
    id: "bleed-and-trim",
    title: {
      pl: "Spad, linia cięcia i szara strefa",
      en: "Bleed, trim line and gray area",
    },
    keywords: {
      pl: ["spad", "bleed", "linia cięcia", "trim", "szara strefa", "pomarańczowa linia", "canvas"],
      en: ["bleed", "trim", "trim line", "gray area", "orange line", "canvas"],
    },
    answer: {
      pl: "Szara strefa pokazuje spad, czyli obszar poza finalnym formatem strony. Tła i zdjęcia, które mają dochodzić do brzegu, powinny wchodzić w spad. Pomarańczowa linia pokazuje linię cięcia. Tekstu nie ustawiaj przy samej krawędzi.",
      en: "The gray area shows bleed, which is outside the final trimmed page. Backgrounds and photos that should reach the edge should extend into bleed. The orange line marks the trim line. Do not place text directly on the edge.",
    },
    related: ["text-guides", "add-background", "background-patterns"],
  },
  {
    id: "casewrap-cover",
    title: {
      pl: "Hardcover: zawijka okładki",
      en: "Hardcover: cover wrap",
    },
    keywords: {
      pl: ["hardcover", "casewrap", "cover wrap", "zawijka", "twarda oprawa", "squeeze", "overlap", "okładka hardcover"],
      en: ["hardcover", "casewrap", "cover wrap", "wrap", "squeeze", "overlap", "hardcover cover"],
    },
    answer: {
      pl: "Dla twardej oprawy canvas okładki jest większy, bo zawiera część drukowaną zawijaną na wewnętrzną stronę okładki. Tło i zdjęcia na pełen brzeg powinny dochodzić aż do zewnętrznej, pomarańczowej linii spadu. Ważny tekst, logo i twarze trzymaj w panelach BACK / SPINE / FRONT, poza strefą zawijania i squeeze przy grzbiecie.",
      en: "For hardcover products the cover canvas is larger because it includes the printed area wrapped around the board. Full-bleed backgrounds and photos should reach the outer orange bleed line. Keep important text, logos and faces inside the BACK / SPINE / FRONT panels, away from the wrap and squeeze zones near the spine.",
    },
    related: ["bleed-and-trim", "spine-title", "background-patterns"],
  },
  {
    id: "spine-title",
    title: {
      pl: "Tytuł na grzbiecie książki",
      en: "Book spine title",
    },
    keywords: {
      pl: ["grzbiet", "spine", "tytuł na grzbiecie", "okładka", "cover", "pokaż tytuł"],
      en: ["spine", "spine title", "cover", "book title", "show title"],
    },
    answer: {
      pl: "Opcje tytułu na grzbiecie są w prawym panelu pod Okładka. Tytuł działa dla okładki typu spread i tylko wtedy, gdy grzbiet ma co najmniej 5 mm. Rozmiar tekstu jest liczony automatycznie z 2 mm paddingu od krawędzi grzbietu.",
      en: "Spine title options are in the right panel under Cover. The title works for spread covers and only when the spine is at least 5 mm wide. Text size is calculated automatically with 2 mm padding from the spine edges.",
    },
    related: ["bleed-and-trim", "save-project"],
  },
  {
    id: "onboarding-guide",
    title: {
      pl: "Przewodnik po edytorze",
      en: "Editor guide",
    },
    keywords: {
      pl: ["przewodnik", "guide", "onboarding", "pierwsze kroki", "pomoc", "podświetlenie"],
      en: ["guide", "onboarding", "first steps", "help", "highlight"],
    },
    answer: {
      pl: "W panelu Pomoc kliknij Przewodnik. Przewodnik pokazuje najważniejsze kroki, podświetla elementy interfejsu i przesuwa okno obok miejsca, o którym mówi.",
      en: "In Help, click Guide. The guide walks through the key steps, highlights interface elements and moves the panel next to the place it describes.",
    },
    related: ["bleed-and-trim", "insert-image", "templates-themes"],
  },
];
